/**
 * Multi-Factor Authentication (MFA) components for setup and management.
 */

import Tab from 'bootstrap/js/dist/tab'

import { configureCopyToClipboard } from './clipboard'
import { initTabSteps } from './tab_steps'

/**
 * Auto-format MFA verification code input to only allow 6 digits
 */
function initMfaCodeInput(): void {
  // Support both field names for backward compatibility
  const mfaInput =
    (document.getElementById('id_mfa_code') as HTMLInputElement) ||
    (document.getElementById(
      'id_two_factor_authentication_code',
    ) as HTMLInputElement)
  if (mfaInput) {
    mfaInput.addEventListener('input', function (e: Event) {
      const target = e.target as HTMLInputElement
      // Only allow numbers
      target.value = target.value.replace(/[^0-9]/g, '')

      // Limit to 6 digits
      if (target.value.length > 6) {
        target.value = target.value.slice(0, 6)
      }
    })
  }
}

/**
 * Initialize MFA copy functionality for secret key and backup codes
 */
function initCopyFunctionality(): void {
  // Configure all copy-to-clipboard elements
  const copyToClipboardElements =
    document.querySelectorAll<HTMLElement>('.copy-to-clipboard')
  for (const element of copyToClipboardElements) {
    configureCopyToClipboard(element)
  }
}

// Replaces the legacy onfocus="this.blur();" inline handler removed for CSP compliance.
function initBlurOnFocus(): void {
  const elements = document.querySelectorAll<HTMLElement>('.js-blur-on-focus')
  for (const element of elements) {
    element.addEventListener('focus', () => element.blur())
  }
}

/**
 * Toggle between OTP and backup code verification methods
 */
function toggleMfaVerificationMethod(): void {
  const otpSection = document.getElementById('mfa-verify-otp-section')
  const backupSection = document.getElementById('mfa-verify-backup-section')
  const switchIcon = document.getElementById('mfa-verify-switch-icon')
  const switchText = document.getElementById('mfa-verify-switch-text')
  const keyIconSource = document.getElementById('mfa-verify-icon-key')
  const mobileIconSource = document.getElementById('mfa-verify-icon-mobile')

  if (!otpSection || !backupSection) return

  const otpField = otpSection.querySelector('input') as HTMLInputElement
  const backupField = backupSection.querySelector('input') as HTMLInputElement

  // Check current visibility
  const isShowingBackup = !backupSection.classList.contains('d-none')

  if (isShowingBackup) {
    // Switch to OTP mode
    otpSection.classList.remove('d-none')
    backupSection.classList.add('d-none')
    if (backupField) backupField.value = '' // Clear backup field

    // Update icon by cloning from hidden source
    if (switchIcon && keyIconSource) {
      switchIcon.innerHTML = keyIconSource.innerHTML
    }
    if (switchText) {
      switchText.textContent = 'I lost my device, use backup code instead'
    }

    // Focus OTP field
    if (otpField) otpField.focus()
  } else {
    // Switch to backup mode
    otpSection.classList.add('d-none')
    backupSection.classList.remove('d-none')
    if (otpField) otpField.value = '' // Clear OTP field

    // Update icon by cloning from hidden source
    if (switchIcon && mobileIconSource) {
      switchIcon.innerHTML = mobileIconSource.innerHTML
    }
    if (switchText) {
      switchText.textContent = 'Use authentication app instead'
    }

    // Focus backup field
    if (backupField) backupField.focus()
  }
}

/**
 * Initialize MFA verification form on the page (if present)
 */
function initMfaVerificationForm(): void {
  const toggleButton = document.getElementById('mfa-verify-toggle')
  if (!toggleButton) return

  // Set up toggle button click handler
  toggleButton.addEventListener('click', toggleMfaVerificationMethod)

  // Auto-focus OTP input
  const otpInput = document
    .getElementById('mfa-verify-otp-section')
    ?.querySelector('input') as HTMLInputElement
  if (otpInput) {
    otpInput.focus()
  }
}

/**
 * Reset the MFA setup modal to its initial state (step 1, cleared inputs).
 */
function resetMfaSetupModal(modal: HTMLElement): void {
  const firstTab = modal.querySelector<HTMLAnchorElement>(
    '.nav a[href="#mfa-setup"]',
  )
  if (firstTab) {
    new Tab(firstTab).show()
  }

  modal.querySelectorAll<HTMLAnchorElement>('.nav-link').forEach((link) => {
    link.classList.remove('valid', 'invalid')
  })

  modal.querySelectorAll<HTMLInputElement>('.otp-digit').forEach((input) => {
    input.value = ''
  })
  const hiddenOtpInput = modal.querySelector<HTMLInputElement>(
    'input[type="hidden"][id="id_otp_code"]',
  )
  if (hiddenOtpInput) {
    hiddenOtpInput.value = ''
  }
}

/**
 * Initialize all MFA components
 */
export function initMfa(): void {
  initMfaCodeInput()
  initCopyFunctionality()
  initBlurOnFocus()
  initMfaVerificationForm()
  initTabSteps({
    container: '#mfa-setup-modal',
    defaultHash: '#mfa-setup',
    updateHash: false, // Don't pollute URL in modal
    scrollToTop: false, // No scroll in modal
    autoFocus: {
      '#mfa-verify': '#mfa-verify .otp-digit',
    },
  })

  const setupModal = document.getElementById('mfa-setup-modal')
  if (setupModal) {
    setupModal.addEventListener('hidden.bs.modal', () =>
      resetMfaSetupModal(setupModal),
    )
  }
}

/**
 * Initialize MFA components for login page setup
 */
export function initMfaLoginSetup(): void {
  initMfaCodeInput()
  initCopyFunctionality()
  initBlurOnFocus()
  initTabSteps({
    container: '#mfa-login-setup',
    defaultHash: '#mfa-setup',
    updateHash: true, // Allow hash navigation on full page
    scrollToTop: true, // Scroll on tab change
    autoFocus: {
      '#mfa-verify': '#mfa-verify input[id*="otp"]',
    },
  })
}
